import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { LineweightMedium } from "@air/next-icons";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SizeAndColorSelect } from "~/components/AssetModal/AssetModalFooter/AnnotationsToggle/AnnotateUtilsPanel/PencilUtils/SizeAndColorSelector/SizeAndColorSelect";
import { AnnotationIconButton } from "~/components/AssetModal/AssetModalFooter/AnnotationsToggle/AnnotateUtilsPanel/shared/AnnotationIconButton";
import { annotationSizes } from "~/components/AssetModal/AssetModalFooter/AnnotationsToggle/AnnotateUtilsPanel/shared/constants";
import { DRAW_ANNOTATION_BUTTON } from "~/constants/testIDs";
import { usePopperContainer } from "~/hooks/usePopperContainer";
import { useAnnotationContext, useAnnotationContextSelector } from "~/providers/AnnotationProvider/AnnotationProvider";
import { annotationColorSelector, annotationSizeSelector, annotationTypeSelector } from "~/providers/AnnotationProvider/annotationProviderUtils";
import { AssetModalPanel, useAssetModalPanelContext } from "~/providers/AssetModalPanelContextProvider";
const PENCIL_COLORS = [
    {
        className: "bg-cockatoo-200/15 text-cockatoo-200 hover:bg-cockatoo-200/15 hover:text-cockatoo-200",
        id: "cockatoo200"
    },
    {
        className: "bg-cuckoo-600/15 text-cuckoo-600 hover:bg-cuckoo-600/15 hover:text-cuckoo-600",
        id: "cuckoo600"
    },
    {
        className: "bg-peacock-400/15 text-peacock-400 hover:bg-peacock-400/15 hover:text-peacock-400",
        id: "peacock400"
    },
    {
        className: "bg-canary-300/15 text-canary-300 hover:bg-canary-300/15 hover:text-canary-300",
        id: "canary300"
    },
    {
        className: "bg-flamingo-400/15 text-flamingo-400 hover:bg-flamingo-400/15 hover:text-flamingo-400",
        id: "flamingo400"
    }
];
const popperOptions = {
    placement: "top-start",
    modifiers: [
        {
            name: "offset",
            options: {
                offset: (param)=>{
                    let { popper } = param;
                    return [
                        -popper.width / 2 + 12,
                        -6
                    ];
                }
            }
        }
    ]
};
export const PencilAnnotationUtils = /*#__PURE__*/ memo(()=>{
    var _annotationSizes_find;
    const { setAnnotationType, setAnnotationColor, setAnnotationSize } = useAnnotationContext();
    const annotationType = useAnnotationContextSelector(annotationTypeSelector);
    const annotationColor = useAnnotationContextSelector(annotationColorSelector);
    const annotationSize = useAnnotationContextSelector(annotationSizeSelector);
    const isActive = annotationType === "pencil";
    const [isSelectorVisible, setIsSelectorVisible] = useState(isActive);
    const containerRef = useRef(null);
    const { panel } = useAssetModalPanelContext();
    const setPencilAnnotationType = useCallback(()=>setAnnotationType("pencil"), [
        setAnnotationType
    ]);
    const hideSelector = useCallback(()=>{
        setIsSelectorVisible(false);
    }, []);
    useEffect(()=>{
        if (isSelectorVisible) {
            window.addEventListener("click", hideSelector);
        }
        return ()=>{
            window.removeEventListener("click", hideSelector);
        };
    }, [
        hideSelector,
        isSelectorVisible
    ]);
    const { PopperContainer, popperInstance } = usePopperContainer({
        containerRef,
        options: popperOptions,
        usePortal: true
    });
    const isAnyTabOpen = useMemo(()=>panel !== AssetModalPanel.NONE, [
        panel
    ]);
    useEffect(()=>{
        // update popper's position when tab appears or disappears
        // timeout is necessary to wait for DOM changes
        setTimeout(()=>{
            var _popperInstance_current;
            return (_popperInstance_current = popperInstance.current) === null || _popperInstance_current === void 0 ? void 0 : _popperInstance_current.update();
        }, 1);
    }, [
        isAnyTabOpen,
        popperInstance
    ]);
    const onPencilBtnClick = useCallback((event)=>{
        event.stopPropagation();
        setIsSelectorVisible((isVisible)=>!isVisible);
        setPencilAnnotationType();
    }, [
        setPencilAnnotationType
    ]);
    var _annotationSizes_find_icon;
    const AnnotationSizeIcon = (_annotationSizes_find_icon = (_annotationSizes_find = annotationSizes.find((param)=>{
        let { width } = param;
        return width === annotationSize;
    })) === null || _annotationSizes_find === void 0 ? void 0 : _annotationSizes_find.icon) !== null && _annotationSizes_find_icon !== void 0 ? _annotationSizes_find_icon : LineweightMedium;
    const pencilColor = PENCIL_COLORS.find((param)=>{
        let { id } = param;
        return id === annotationColor;
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center",
        children: [
            /*#__PURE__*/ _jsx(AnnotationIconButton, {
                className: isActive ? pencilColor === null || pencilColor === void 0 ? void 0 : pencilColor.className : undefined,
                "data-testid": DRAW_ANNOTATION_BUTTON,
                label: "Draw annotation",
                onClick: onPencilBtnClick,
                ref: containerRef,
                children: /*#__PURE__*/ _jsx(AnnotationSizeIcon, {
                    className: "size-4"
                })
            }),
            isSelectorVisible && /*#__PURE__*/ _jsx(_Fragment, {
                children: /*#__PURE__*/ _jsx(PopperContainer, {
                    children: /*#__PURE__*/ _jsx(SizeAndColorSelect, {
                        selectedColor: annotationColor,
                        setColor: setAnnotationColor,
                        selectedSize: annotationSize,
                        setSize: setAnnotationSize
                    })
                })
            })
        ]
    });
});
PencilAnnotationUtils.displayName = "PencilAnnotationUtils";
